<template>
  <div class="table-wrapper">
    <div class="output-presets-main d-flex flex-wrap">
      <div class="mr-2 d-flex mb-1 mb-md-0">
        <b-button
          v-if="$can('create', 'presets', 'encoder')"
          variant="bg-primary"
          :to="{ name: 'output-presets-new' }"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create
        </b-button>
        <b-form-file
          ref="pickJson"
          v-model="jsonFile"
          class="d-none"
        />
        <b-button
          v-if="$can('create', 'presets', 'encoder')"
          variant="bg-white"
          :disabled="importingJson"
          @click="uploadJsonModal = !uploadJsonModal"
        >
          <GjIcon
            name="Download"
            class="icon-left"
            size="20"
          />
          Import JSON
        </b-button>
        <b-button
          v-if="$can('list', 'presets', 'encoder')"
          variant="bg-white"
          class="d-none d-lg-inline"
          :disabled="!isSelected || exportingJson"
          @click="isSelected ? exportJson() : ''"
        >
          <GjIcon
            name="Redirect"
            class="icon-left"
            size="20"
          />
          Export JSON
        </b-button>
      </div>
      <b-button
        v-if="$can('get', 'presets', 'encoder')"
        variant="bg-white"
        :disabled="!isSinglePresetSelected"
        class="d-none d-lg-inline"
        @click="
          isSinglePresetSelected
            ? $router.push({
              name: 'output-presets-view',
              params: { id: selected[0].id },
            })
            : ''
        "
      >
        View
      </b-button>
      <b-button
        v-if="$can('create', 'presets', 'encoder')"
        variant="bg-white"
        :disabled="!isSinglePresetSelected"
        class="d-none d-lg-inline"
        @click="duplicateOutputPreset"
      >
        Clone
      </b-button>
      <b-button
        v-if="$can('update', 'presets', 'encoder')"
        variant="bg-white"
        :disabled="!selectedIsCustom"
        class="d-none d-lg-inline"
        @click="
          selectedIsCustom
            ? $router.push({
              name: 'output-presets-edit',
              params: {
                id: selected[0].id,
              },
            })
            : ''
        "
      >
        Edit
      </b-button>
      <b-button
        v-if="$can('delete', 'presets', 'encoder')"
        class="d-none d-lg-inline"
        variant="bg-white"
        :disabled="!selectedIsCustom"
        @click="() => (selectedIsCustom ? deleteOutputPreset() : '')"
      >
        Delete
      </b-button>
    </div>

    <div
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <div class="table-actions">
        <div class="d-flex">
          <b-dropdown
            id="
          output-preset-filter-dropdown-1"
            :text="presetType.value"
            variant="bg-white"
            class="dropdown-icon-wrapper"
          >
            <template #button-content>
              <span>{{ presetType.value }}</span>
              <GjIcon
                class="icon-right"
                name="ArrowDown"
              />
            </template>
            <template v-if="presetTypeOptions && !fetchingOutputPresetFilters">
              <b-dropdown-item
                v-for="type in presetTypeOptions"
                :key="type.id"
                @click="presetType = type"
              >
                {{ type.value }}
              </b-dropdown-item>
            </template>
          </b-dropdown>
          <b-input-group
            v-b-tooltip.hover.v-primary
            class="searchInputGroup ml-auto"
            title="Search by preset name, description or category
"
          >
            <b-input-group-prepend is-text>
              <b-icon-search @click="searchInputChange" />
            </b-input-group-prepend>
            <b-form-input
              id="filterInput"
              v-model.trim="searchInput"
              type="search"
              class="searchInput"
              placeholder="Search by preset name"
              @change="searchInputChange"
            />
          </b-input-group>
        </div>
      </div>
      <!-- DESKTOP -->
      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="table"
          responsive
          :select-mode="selectMode"
          :items="presets"
          :fields="fields"
          :class="{ loaded: !fetchingOutputPresets }"
          show-empty
          selectable
          @row-selected="onRowSelected"
        >
          <template #cell(selected)="{ rowSelected }">
            <select-button :selected="rowSelected" />
          </template>

          <template #cell(name)="data">
            <router-link
              :to="{
                name: 'output-presets-view',
                params: {
                  id: data.item.id,
                },
              }"
            >
              {{ data.value }}
            </router-link>
          </template>

          <template #cell(presetType)="data">
            {{ getObjectOptionsText(presetTypes, data.value) }}
          </template>

          <template #empty>
            <template v-if="fetchingOutputPresets">
              <div class="loading-table text-center py-3">
                <b-spinner variant="primary" />
              </div>
            </template>
            <template v-else>
              <no-data
                no-data-text="No output presets have been added yet..."
                create-text="+ Create a custom preset"
                access-page="Output presets"
                icon="Section"
                :has-permissions="hasPermissions"
                @action="$router.push({ name: 'output-presets-new' })"
              />
            </template>
          </template>
        </b-table>
        <table-busy
          v-if="
            (fetchingOutputPresets && presets.length > 0) ||
              deletingOutputPreset
          "
        />
      </div>
      <!-- MOBILE -->
      <div class="position-relative mt-1 d-lg-none">
        <div :class="{ 'opacity-7': fetchingOutputPresets }">
          <template v-for="preset in presets">
            <card
              :key="preset.id"
              :fields="fields"
              :item="preset"
              class="top-fade-in"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                  @show="select(preset)"
                  @hide="select(null)"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item @click="exportJson([preset])">
                    Export JSON
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('get', 'presets', 'encoder')"
                    @click="
                      $router.push({
                        name: 'output-presets-view',
                        params: { id: preset.id },
                      })
                    "
                  >
                    View
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('create', 'presets', 'encoder')"
                    @click="duplicateOutputPreset"
                  >
                    Duplicate
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('update', 'presets', 'encoder')"
                    :disabled="!selectedIsCustom"
                    @click="
                      $router.push({
                        name: 'output-presets-edit',
                        params: { id: preset.id },
                      })
                    "
                  >
                    Edit
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('delete', 'presets', 'encoder')"
                    :disabled="!selectedIsCustom"
                    @click="deleteOutputPreset"
                  >
                    Delete
                  </b-dropdown-item>
                </b-dropdown>
              </template>
              <template #name>
                <router-link
                  :to="{
                    name: 'output-presets-view',
                    params: {
                      id: preset.name,
                      projectId: $route.params.projectId,
                    },
                  }"
                >
                  {{ preset.name }}
                </router-link>
              </template>
              <template #presetType>
                {{ getObjectOptionsText(presetTypes, preset.presetType) }}
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="fetchingOutputPresets"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!fetchingOutputPresets && presets.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No output presets have been added yet..."
              create-text="+ Create a custom preset"
              access-page="Output presets"
              icon="Section"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'output-presets-new' })"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="presets.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
    <b-modal
      v-model="uploadJsonModal"
      centered
      cancel-variant="bg-white"
      ok-title="Import"
      :ok-disabled="!jsonFile || creatingOutputPreset"
      title="Import JSON"
      body-class="action-modal"
      @ok.prevent="importJson"
    >
      <div :class="creatingOutputPreset ? 'blur-modal-content' : ''">
        <input-field
          name="output-preset-json"
          label="Output Preset JSON File"
          label-position="top"
        >
          <b-form-file
            v-model="jsonFile"
            accept="application/json"
            placeholder="Select json file..."
          />
        </input-field>
      </div>
      <div
        v-if="creatingOutputPreset"
        class="d-flex position-absolute modal-loading justify-content-center align-items-center"
      >
        <b-spinner variant="primary" />
      </div>
    </b-modal>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'
import fileHelperMixin from '@/mixins/file-helpers'
import InputField from '@/components/form/InputField.vue'
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'OutputPresets',
  components: {
    InputField,
    Card,
    BIconSearch,
  },
  mixins: [fileHelperMixin],
  data() {
    return {
      selectMode: 'multi',
      uploadJsonModal: false,
      jsonFile: null,
      presetType: { value: 'All Presets ', id: -1 },
      selectedCategory: { value: 'Any Category', id: 0 },
      searchInput: '',
      originsIndex: 1,
      cnamesIndex: 1,
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'name', sortable: true, isPrimary: true },
        { key: 'description', isPrimary: true },
        'category',
        { key: 'presetType', label: 'Type', variant: 'small' },
      ],
      selected: [],
      exportingJson: false,
      importingJson: false,
      hasPermissions: true,
      presetTypes: {
        System: 0,
        Custom: 1,
      },
    }
  },
  computed: {
    creatingOutputPreset() {
      return this.$store.state.outputPresets.creatingOutputPreset
    },
    selectedIsCustom() {
      return this.isSinglePresetSelected
        ? this.selected[0].presetType === this.presetTypes.Custom
        : false
    },
    isSinglePresetSelected() {
      if (this.selected.length === 1) {
        return true
      }
      return false
    },
    isSelected() {
      return this.selected.length > 0
    },
    presets() {
      return this.$store.state.outputPresets.outputPresets
    },
    presetTypeOptions() {
      return (
        this.$store.state.outputPresets.outputPresetFilters?.presetOptions || []
      )
    },
    fetchingOutputPresetFilters() {
      return this.$store.state.outputPresets.fetchingOutputPresetFilters
    },
    resultInfo() {
      return this.$store.state.outputPresets.outputPresetsResultInfo
    },
    fetchingOutputPresets() {
      return this.$store.state.outputPresets.fetchingOutputPresets
    },
    deletingOutputPreset() {
      return this.$store.state.outputPresets.deletingOutputPreset
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    currentPage: {
      get() {
        return this.$store.getters['outputPresets/currentPage']
      },
      set(val) {
        this.$store.commit('outputPresets/SET_CURRENT_PAGE', val)
      },
    },
  },
  watch: {
    currentPage() {
      this.fetchOutputPresets()
    },
    presetType() {
      if (this.currentPage === 1) {
        this.fetchOutputPresets()
      } else {
        this.currentPage = 1
      }
    },
    selectedCategory() {
      if (this.currentPage === 1) {
        this.fetchOutputPresets()
      } else {
        this.currentPage = 1
      }
    },

    uploadJsonModal(val) {
      if (!val) {
        this.jsonFile = null
      }
    },
  },
  created() {
    if (this.$store.state.app.previousRouteName !== 'output-presets-edit') {
      this.currentPage = 1
    }
    this.fetchOutputPresets()
    this.fetchOutputPresetFilters()
    if (this.$route.params.toast) {
      const { toast } = this.$route.params
      this.generalToast(toast.message, toast.title, toast.variant)
    }
  },
  mounted() {
    this.isMounted = true
  },
  methods: {
    select(job) {
      this.selected = job !== null ? [job] : []
    },
    searchInputChange() {
      if (this.currentPage === 1) {
        this.fetchOutputPresets()
      } else {
        this.currentPage = 1
      }
    },
    importJson(e) {
      e.preventDefault()
      if (this.jsonFile) {
        this.getFileContent(this.jsonFile)
          .then(res => {
            const tempObj = JSON.parse(res)
            this.createOutputPresets(tempObj)
          })
          .catch(() => {
            this.errorToast('Something went wrong.')
          })
      }
    },
    exportJson(arr) {
      this.exportMultiplePresets(arr || this.selected)
    },
    exportMultiplePresets(arr) {
      this.exportingJson = true
      const exportRequests = arr.map(el => this.getPresetContent(el.id))
      const tempDate = new Date()
      const fileName = `Exported_Presets_${tempDate.toDateString()}_${tempDate.getHours()}_${tempDate.getMinutes()}`
      Promise.all(exportRequests)
        .then(res => {
          this.objectToJsonFile(res, fileName)
        })
        .catch(() => {
          this.errorToast('Error exporting presets')
        })
        .finally(() => {
          this.exportingJson = false
        })
    },
    getPresetContent(id) {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch('outputPresets/getOutputPresetByIdNoCommit', id)
          .then(res => {
            resolve(res.data)
          })
          .catch(err => reject(err))
      })
    },
    onRowSelected(items) {
      this.selected = items
    },
    deleteOutputPreset() {
      this.confirmDelete(
        'Delete output preset',
        'Are you sure you want to delete this output preset?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('outputPresets/deleteOutputPreset', this.selected[0].id)
            .then(() => {
              this.successToast('Output preset deleted successfully')
            })
            .catch(() => {
              this.errorToast(
                "An error occurred, couldn't delete output preset",
              )
            })
        }
      })
    },
    duplicateOutputPreset() {
      this.$router.push({
        name: 'output-presets-new',
        params: {
          presetId: this.selected[0].id,
          cloned: true,
        },
      })
    },
    fetchOutputPresets() {
      this.$store
        .dispatch('outputPresets/fetchOutputPresets', {
          type: this.presetType.id,
          pageIndex: this.currentPage - 1,
          category:
            this.presetType.id === 0 && this.selectedCategory.id !== 0
              ? this.selectedCategory.value
              : null,
          search: this.searchInput,
        })
        .catch(() => {
          this.errorToast("Couldn't fetch output presets.")
        })
    },
    fetchOutputPresetFilters() {
      this.$store
        .dispatch('outputPresets/fetchOutputPresetFilters')
        .then(() => {
          this.presetType = this.presetTypeOptions.find(e => e.id === -1)
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast("Couldn't fetch output presets.")
        })
    },
    createOutputPreset(e) {
      this.$store
        .dispatch('outputPresets/createOutputPreset', e)
        .then(() => {
          this.fetchOutputPresets()
          this.successToast('Output Preset imported successfully')
          this.uploadJsonModal = false
        })
        .catch(() => {
          this.errorToast("An error occurred, couldn't import output preset.")
        })
    },
    createOutputPresets(arr) {
      this.importingJson = true
      const importRequests = arr.map(el => this.$store.dispatch('outputPresets/createOutputPreset', el))
      Promise.all(importRequests)
        .then(() => {
          this.fetchOutputPresets()
          this.successToast('Output Presets json imported successfully')
        })
        .catch(() => {
          this.errorToast('Error importing Output Presets json')
        })
        .finally(() => {
          this.importingJson = false
          this.uploadJsonModal = false
        })
    },
  },
}
</script>

<style scoped>
.searchInputGroup {
  width: 250px;
}
.searchInput {
  margin-right: 0;
}
</style>
